/* eslint-disable react/button-has-type */
/* eslint-disable react/display-name */
/* eslint-disable react/jsx-no-bind */
import React, {
  useState, useRef, useEffect, useMemo,
} from 'react';
import Layout from 'antd/lib/layout/layout';
import {
  Spin,
  Pagination,
  Input,
  Breadcrumb,
  Empty,
  Tabs,
  Tooltip,
} from 'antd';
import {
  useHistory,
  Switch,
  Route,
} from 'react-router-dom';

import {
  FormikValues,
} from 'formik';

import { SearchOutlined } from '@ant-design/icons';

import useTenantData from './useTenantData';
import './tenant.less';
import PageTitle from '../../lib/page-title/page-title';
import breadcrumbSeparator from '../../../assets/icons/breadcrumbSeparator.svg';
import capitalize from '../../../utils/capitalize';
import Card from '../../lib/card';
import FilterSelect from '../../lib/filter-select';
import { IFormFields } from '../../lib/field-edit-form/field-edit-form';
import BreadcrumbItem from '../../lib/breadcrumbItem';
import { iconNames, IGroupsTree } from '../../../store/modules/facility/i-facility';
import SecondaryButton from '../../lib/secondary-button';
import PrimaryButton from '../../lib/primary-button';
import { useStore } from '../../../store';
import FieldArrayRef from '../../../utils/FieldArrayRef';
import { useActions } from '../../providers/actions-provider/actions.context';
import ZoneListForm from './zone.list.form';
import PermissionProvider from '../../providers/permissionProvider';
import checkPermission from '../../../utils/permissions/permissions';
import FacilityTable from './facility.list';
import ZoneMaker, { IZoneMakerRef } from './zoneMaker';
import { useLocalStorage } from '../../providers/local-storage-provider/local-storage.context';

const groupsFilter: Array<IFormFields> = [
  { name: 'Name', value: 'name' },
  { name: 'Code', value: 'code' },
];

const Tenant = (): JSX.Element => {
  const {
    data,
    groupTitle,
    zones,
    zonesNew,
    loading,
    groupType,
    isAFirstPage,
    currentGroup,
    currentGroupID,
    zonesFilter,
    routes,
    zonesSaveState,
  } = useTenantData();

  const hasNotWritePermission = !checkPermission('facilities', 'write');
  const { tenantActions, facilityActions } = useActions();
  const { push, replace, location } = useHistory();
  const { groupTypes, mapImageImage } = useStore();
  const localStorageService = useLocalStorage();

  // Restore the groups filter from the URL so it survives navigating into a
  // facility and back - the route remounts `Tenant` on every navigation
  // (keyed by pathname), so local state alone would always reset.
  const [currentGroupsFilter, setCurrentGroupsFilter] = useState<string>(
    () => new URLSearchParams(location.search).get('field') || 'name',
  );
  const [searchGroupsValue, setSearchGroupsValue] = useState<string>(
    () => new URLSearchParams(location.search).get('keyword') || '',
  );

  const displayedData = useMemo(() => {
    if (!Array.isArray(data) || !searchGroupsValue || !currentGroupsFilter) {
      return data;
    }
    const keyword = searchGroupsValue.toLowerCase();
    return data.filter((group) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value = (group as any)[currentGroupsFilter];
      return typeof value === 'string' && value.toLowerCase().indexOf(keyword) > -1;
    });
  }, [data, searchGroupsValue, currentGroupsFilter]);

  const dataLength = Array.isArray(displayedData) && displayedData.length;
  const resolvedGroupTitle = isAFirstPage && dataLength !== false ? `${groupTitle} (${dataLength} total)` : groupTitle;
  const [currentZonesFilter, setCurrentZonesFilter] = useState<string>('');
  const [searchZonesValue, setSearchZonesValue] = useState<string>('');

  const [, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(24);
  const [skip, setSkip] = useState(0);
  const [activeTabKeyZone, setActiveTabKeyZone] = useState<string>('1');
  const [activeTabKeyFacility, setActiveTabKeyFacility] = useState<string>(
    () => (localStorageService.get<string>('FACILITY_TAB') === 'grid' ? 'grid' : 'table'),
  );
  const { showZones } = zonesNew;
  const facilityTableView = activeTabKeyFacility === 'table';

  // eslint-disable-next-line
  const zonesFieldArrayRef = useRef<FieldArrayRef<any>>(null);
  const hasImage = mapImageImage.data instanceof Blob;

  // eslint-disable-next-line
  const zoneMakerFieldArrayRef = useRef<FieldArrayRef<any>>(null);
  const zoneMakerRef = useRef<IZoneMakerRef>(null);
  const zonesContainerRef = useRef<HTMLDivElement>(null);
  const [disableSave, setDisableSave] = useState<boolean>(true);

  const syncGroupsFilterToUrl = (keyword: string, field: string): void => {
    const nextParams = new URLSearchParams();
    if (keyword) {
      nextParams.set('field', field);
      nextParams.set('keyword', keyword);
    }
    const nextSearch = nextParams.toString();
    const currentSearch = location.search.replace(/^\?/, '');
    if (nextSearch !== currentSearch) {
      replace(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`);
    }
  };

  const handleGroupsChange = (e: string) => {
    setCurrentGroupsFilter(e);
    const nextKeyword = e ? searchGroupsValue : '';
    if (!e) {
      setSearchGroupsValue('');
    }
    syncGroupsFilterToUrl(nextKeyword, e);
  };

  const onGroupsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchGroupsValue(e.target.value);
    syncGroupsFilterToUrl(e.target.value, currentGroupsFilter);
  };

  const onZonesSearch = (value: string, filter: string) => {
    setSearchZonesValue(value);
    setCurrentZonesFilter(filter);
  };

  const handleZonesChange = (e: string) => {
    setCurrentZonesFilter(e);
    onZonesSearch(searchZonesValue, e);
  };

  const handleCreateNewZone = () => {
    if (activeTabKeyZone === '1') {
      zonesFieldArrayRef.current?.addItem({
        name: '',
        propertiesAsText: '',
        logicalReadersAsText: '',
        polygonsAsText: '',
      });
    } else {
      zoneMakerFieldArrayRef.current?.addItem({
        name: '',
        propertiesAsText: '',
        logicalReadersAsText: '',
        polygonsAsText: '',
      });
      setTimeout(() => {
        if (zonesContainerRef.current) {
          zonesContainerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
          });
          zonesContainerRef.current.scrollTop = zonesContainerRef.current.scrollHeight;
          const inputElements = zonesContainerRef.current.querySelectorAll<HTMLInputElement>(
            '.zone-maker-input',
          );
          if (inputElements.length > 0) {
            const lastInput = inputElements[inputElements.length - 1];
            (lastInput as HTMLInputElement)?.focus();
          }
        }
      }, 0);
    }
  };

  const onZonesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchZonesValue(e.target.value);
    onZonesSearch(e.target.value, currentZonesFilter);
  };
  let groupTypeID = null;
  if (groupType?.groupType_id) {
    groupTypeID = groupType?.groupType_id;
  }

  const tabsStylesZone = activeTabKeyZone === '1' ? { minHeight: '100vh' } : {};
  const tabsStylesFacility = facilityTableView ? { minHeight: '100vh' } : {};

  const handleSubmitZoneTable = async (values: FormikValues) => {
    await tenantActions.saveZones(values.zones, currentGroup?.id);
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    await facilityActions.getZones((currentGroup?.id)!.toString());
  };

  useEffect(() => {
    if (currentGroup?.id) {
      facilityActions.getMapImage(currentGroup.id);
    }
  }, [currentGroup?.id]);

  return (
    <Layout className="main-container">
      <PermissionProvider pageId="facilities">
        <Breadcrumb
          itemRender={BreadcrumbItem}
          routes={routes}
          separator={<img src={breadcrumbSeparator} alt="breadcrumb separator" />}
          params={{
            routes,
          }}
        />

        <PageTitle
          back={!isAFirstPage}
          title={loading ? '' : resolvedGroupTitle}
        >
          {!loading
          && (
            <div>
              {!isAFirstPage && <SecondaryButton className="button button__transparent left" onClick={() => push(`${location.pathname}/editGroup?group=${currentGroup?.id || 'root'}&groupType=${currentGroup?.groupType_id || 'root'}&children=${currentGroup?.children?.length ?? 0}`)}>Details</SecondaryButton>}
              {groupTypeID === null && (groupType && !showZones && <PrimaryButton disabled={hasNotWritePermission} onClick={() => push(`${location.pathname}/createGroup?group=${currentGroup?.id || 'root'}&groupType=${groupType?.groupType_id ?? 'root'}`)} title={`New ${capitalize(groupType?.name)}`} />)}
              {groupTypeID && groupTypeID === groupTypes.data[groupTypeID].id
                ? (groupType && !showZones && <PrimaryButton onClick={() => push(`${location.pathname}/createGroup?group=${currentGroup?.id || 'root'}&groupType=${groupType?.groupType_id ?? 'root'}`)} title={`New ${capitalize(groupType?.name)}`} />)
                : ((showZones || !Array.isArray(data)) && (
                  <Tooltip
                    title={
                      !hasImage
                        ? 'Please upload facility floor plan before creating zones'
                        : ''
                    }
                  >
                    <span>
                      <PrimaryButton
                        disabled={hasNotWritePermission || !hasImage}
                        onClick={handleCreateNewZone}
                        title="New Zone"
                      />
                    </span>
                  </Tooltip>
                ))}
            </div>
          )}
        </PageTitle>
        {loading ? (
          <Spin size="large" />
        ) : (
          <>
            <div className="users__filters">
              {showZones ? (
                <>
                  <FilterSelect
                    currentFilter={currentGroupsFilter}
                    onChange={(e) => handleZonesChange(e)}
                    data={zonesFilter}
                    value={currentZonesFilter || undefined}
                  />
                  <Input
                    className="search"
                    placeholder="Keyword"
                    onChange={onZonesChange}
                    value={searchZonesValue}
                    suffix={<SearchOutlined style={{ color: '#D0D3D9' }} />}
                  />
                </>
              ) : (
                <>
                  <FilterSelect
                    currentFilter={currentGroupsFilter}
                    value={currentGroupsFilter}
                    onChange={(e) => handleGroupsChange(e)}
                    data={groupsFilter}
                  />
                  <Input
                    className="search"
                    placeholder="Keyword"
                    value={searchGroupsValue}
                    onChange={onGroupsChange}
                    suffix={<SearchOutlined style={{ color: '#D0D3D9' }} />}
                  />
                </>
              )}
            </div>

            {!Array.isArray(data) && Boolean(!zones.data.data.length)
            && (<Empty style={{ justifySelf: 'center' }} image={Empty.PRESENTED_IMAGE_SIMPLE} />)}

            {
              !showZones && (
                <Tabs
                  activeKey={activeTabKeyFacility}
                  onChange={(tabKey: string) => { setActiveTabKeyFacility(tabKey); localStorageService.set('FACILITY_TAB', tabKey); }}
                  style={tabsStylesFacility}
                >
                  <Tabs.TabPane tab="Table view" key="table">
                    <FacilityTable data={displayedData as IGroupsTree[]} />
                  </Tabs.TabPane>
                  <Tabs.TabPane tab="Grid view" key="grid">
                    { Array.isArray(displayedData) && (
                      <section className="cards">
                        {
                          displayedData.slice(skip, pageSize).map(((group) => (
                            <Card
                              key={group.id}
                              onClick={() => {
                                push(`${location.pathname}/${group.id}`);
                              }}
                              onDetailsClick={(e) => {
                                e.stopPropagation();
                                push(`${location.pathname}/editGroup?group=${group?.id || 'root'}&groupType=${group?.groupType_id ?? 'root'}`);
                              }}
                              title={group.name}
                              id={group.id}
                              region={capitalize(group.groupType_name)}
                              icon={groupType?.icon ?? iconNames.icon1}
                            />
                          )))
                        }
                      </section>
                    )}
                  </Tabs.TabPane>
                </Tabs>
              )
            }

            {showZones && (
              <div className="tenant-zone-tabs-container">
                <Tabs
                  activeKey={activeTabKeyZone}
                  onChange={(tabKey: string) => {
                    setActiveTabKeyZone(tabKey);
                    if (tabKey === '2') {
                      onZonesSearch('', '');
                    }
                  }}
                  style={tabsStylesZone}
                >
                  <Tabs.TabPane tab="Table view" key="1">
                    <ZoneListForm
                      zones={zonesNew.data}
                      loading={zonesSaveState.loading}
                      onSubmit={(values) => handleSubmitZoneTable(values)}
                      fieldArrayRef={zonesFieldArrayRef}
                      currentZonesFilter={currentZonesFilter}
                      searchZonesValue={searchZonesValue}
                    />
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={
                      !hasImage ? (
                        <Tooltip title="Please upload facility floor plan before accessing Zone Maker">
                          <span>Zone Maker</span>
                        </Tooltip>
                      ) : (
                        'Zone Maker'
                      )
                    }
                    key="2"
                    disabled={!hasImage}
                  >
                    <ZoneMaker
                      key={currentGroupID}
                      ref={zoneMakerRef}
                      groupId={currentGroupID}
                      fieldArrayRef={zoneMakerFieldArrayRef}
                      setDisableSave={setDisableSave}
                      existingZones={zonesNew.data}
                      hasUnsavedChanges={!disableSave}
                      zonesContainerRef={zonesContainerRef}
                      currentZonesFilter={currentZonesFilter}
                      searchZonesValue={searchZonesValue}
                    />
                  </Tabs.TabPane>
                </Tabs>
                { activeTabKeyZone === '2' && (
                  <PrimaryButton
                    className="tenant-save-changes-button"
                    disabled={
                      hasNotWritePermission
                      || disableSave
                    }
                    title="Save changes"
                    onClick={() => (zoneMakerRef.current && zoneMakerRef.current.submitForm())}
                  />
                )}
              </div>
            )}

            {!showZones && Boolean(dataLength) && !facilityTableView && (
              <div className="pagination__wrapper">
                <Pagination
                  className="custom-pagination"
                  defaultCurrent={1}
                  total={Array.isArray(displayedData) ? displayedData.length : 0}
                  defaultPageSize={24}
                  hideOnSinglePage
                  onChange={(pageNumber, pageSizeNumber = 1) => {
                    if (pageSizeNumber) {
                      const skipNumber = (pageNumber - 1) * pageSizeNumber;
                      setPage(pageNumber);
                      setPageSize(pageSizeNumber * pageNumber);
                      setSkip(skipNumber);
                    }
                  }}
                />
              </div>
            )}

            <Switch>
              <Route path={`${location.pathname}/:id`} component={Tenant} exact />
            </Switch>
          </>
        )}
      </PermissionProvider>
    </Layout>
  );
};

Tenant.displayName = 'Tenants';

export default Tenant;
